import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, resolveImage } from "@/lib/api";
import { Card, Btn, Badge, AInput, ATextarea, ASelect } from "../AdminUI";
import MediaPicker from "@/components/admin/MediaPicker";
import { Plus, Trash2 } from "lucide-react";

export default function ServicesAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/services?all=true").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const blank = { name: "", slug: "", short_description: "", long_description: "", image: "", status: "draft", surfaces: [], benefits: [] };
  const saveS = async () => {
    try {
      const body = { ...edit, slug: edit.slug || edit.name.toLowerCase().replace(/[^a-z0-9]+/g, "-") };
      if (edit.id) await api.patch(`/admin/services/${edit.id}`, body);
      else await api.post("/admin/services", body);
      toast.success("Saved"); setEdit(null); load();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this service?")) return; await api.delete(`/admin/services/${id}`); load(); };
  const toggle = async (s) => { await api.patch(`/admin/services/${s.id}`, { status: s.status === "active" ? "draft" : "active" }); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">Services</h1>
        <Btn onClick={() => setEdit(blank)} data-testid="add-service"><Plus className="w-4 h-4" /> Add Service</Btn>
      </div>

      {edit && (
        <Card title={edit.id ? "Edit Service" : "New Service"}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <AInput label="Name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
            <AInput label="Slug (optional)" value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} />
            <ASelect label="Status" value={edit.status} options={["active", "draft"]} onChange={(e) => setEdit({ ...edit, status: e.target.value })} />
            <div className="sm:col-span-2">
              <MediaPicker
                label="Service Image"
                value={edit.image || ""}
                onChange={(ref) => setEdit({ ...edit, image: ref })}
                allowCapture
                allowExternalUrl
                previewClass="h-44"
                hint="Upload, take a photo, or pick from the Media Library. Existing image paths keep working."
                testid="service-image"
              />
            </div>
            <div className="sm:col-span-2"><ATextarea label="Short Description" value={edit.short_description} onChange={(e) => setEdit({ ...edit, short_description: e.target.value })} /></div>
            <div className="sm:col-span-2"><ATextarea label="Long Description" value={edit.long_description} onChange={(e) => setEdit({ ...edit, long_description: e.target.value })} /></div>
            <AInput label="Surfaces (comma separated)" value={(edit.surfaces || []).join(", ")} onChange={(e) => setEdit({ ...edit, surfaces: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
            <AInput label="Benefits (comma separated)" value={(edit.benefits || []).join(", ")} onChange={(e) => setEdit({ ...edit, benefits: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
          </div>
          <div className="flex gap-2 mt-4"><Btn onClick={saveS} data-testid="save-service">Save</Btn><Btn variant="ghost" onClick={() => setEdit(null)}>Cancel</Btn></div>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <Card key={s.id}>
            <img src={resolveImage(s.image)} alt={s.name} className="h-32 w-full object-cover rounded-lg mb-3" />
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-[var(--navy)]">{s.name}</h3>
              <Badge color={s.status === "active" ? "green" : "amber"}>{s.status}</Badge>
            </div>
            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{s.short_description}</p>
            <div className="flex gap-2 mt-3">
              <Btn variant="ghost" onClick={() => setEdit(s)} className="flex-1">Edit</Btn>
              <Btn variant="blue" onClick={() => toggle(s)}>{s.status === "active" ? "Unpublish" : "Publish"}</Btn>
              <Btn variant="danger" onClick={() => del(s.id)}><Trash2 className="w-4 h-4" /></Btn>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
